/**
 * Thin axios wrapper for the candidate-facing "My Responses" view
 * (candidate-attribution / outreach MVP).
 *
 * A CANDIDATE user reads the per-candidate leans recorded at the door for
 * THEIR OWN race. The backend scopes the result to the candidate the caller is
 * linked to and rejects a foreign candidate_id (403); this module is read-only
 * and never mutates.
 *
 *   GET /candidates/{id}/leans — the door-knock leans attributed to a candidate,
 *                                newest first, each with a minimal voter card.
 *
 * Axios errors are intentionally left to propagate — call sites read
 * `err.response?.data?.detail` to surface the server's message. Pattern mirrors
 * `src/api/delegation.ts`.
 */
import client from '@/api/client';

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/**
 * The lean a voter expressed toward a candidate. Wire spelling matches the
 * `knock_response_candidate_leans.lean` enum — snake_case, never a label.
 */
export type CandidateLean =
  | 'strong_support'
  | 'lean_support'
  | 'undecided'
  | 'lean_oppose'
  | 'strong_oppose'
  | 'refused';

/** How the lean was captured: on a presented slate vs volunteered at the door. */
export type LeanSource = 'slate' | 'unsolicited';

/** The minimal voter card that rides along with each lean row. */
export interface CandidateLeanVoter {
  first_name: string | null;
  last_name: string | null;
  address: string | null;
  precinct_num: string | null;
}

/**
 * One row of `GET /candidates/{id}/leans`: a single lean recorded at a door,
 * plus the voter it came from. `created_at` may be null for legacy rows.
 */
export interface CandidateLeanRow {
  knock_response_id: number;
  lean: CandidateLean;
  source: LeanSource;
  created_at: string | null;
  voter: CandidateLeanVoter;
}

// ---------------------------------------------------------------------------
// Endpoint
// ---------------------------------------------------------------------------

/**
 * List the leans attributed to a candidate, newest first. Lets axios errors
 * propagate so the caller can surface `err.response?.data?.detail` (403 when
 * the candidate isn't the caller's own race).
 */
export async function getCandidateLeans(
  candidateId: number,
): Promise<CandidateLeanRow[]> {
  const res = await client.get<CandidateLeanRow[]>(
    `/candidates/${candidateId}/leans`,
  );
  return res.data;
}
